import { BookOpen, Users, GraduationCap, Calendar, ArrowLeft, UserCheck, UserPlus, Clock, Coffee, Timer, Layers, ToggleLeft, ToggleRight, Info, ChevronRight } from "lucide-react";
import TeacherLessonAssignments from "./components/TeacherLessonAssignments";
import TeacherBranchAssignments from "./components/TeacherBranchAssignments";
import ClassLessonAssignments from "./components/ClassLessonAssignments";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { usePanels } from "../../contexts/PanelContext.jsx";
import ConfirmModal from "../../components/modals/ConfirmModal";
import TeacherSchedule from "./components/TeacherSchedule";
import TeachersList from "./components/TeachersList";
import StudentsList from "./components/StudentsList";
import DailyLessons from "./components/DailyLessons";
import LessonsList from "./components/LessonsList";
import React, { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

interface ScheduleSettings {
  workDaysPerWeek: number | string;
  maxHoursPerDay: number | string;
  minBreakBetweenLessons: number | string;
  autoDistribute: boolean;
  considerTeacherPreferences: boolean;
  balanceWorkload: boolean;
  avoidConsecutiveLessons: boolean;
  maxConsecutiveHours: number | string;
  maxDailySameLesson: number | string;
  lessonDuration: number | string;
  breakDuration: number | string;
  schoolStartTime: string;
  lunchBreakStart: string;
  lunchBreakDuration: number | string;
}

const Planning = () => {
  const navigate = useNavigate();
  const { mainContentRightInsetActive } = usePanels();
  const [activeView, setActiveView] = useState("dashboard");
  const [savingSettings, setSavingSettings] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [settings, setSettings] = useState<ScheduleSettings>({
    workDaysPerWeek: 5,
    maxHoursPerDay: 8,
    minBreakBetweenLessons: 1,
    autoDistribute: true,
    considerTeacherPreferences: true,
    balanceWorkload: true,
    avoidConsecutiveLessons: false,
    maxConsecutiveHours: 3,
    maxDailySameLesson: 0,
    lessonDuration: 40,
    breakDuration: 10,
    schoolStartTime: '08:30',
    lunchBreakStart: '12:00',
    lunchBreakDuration: 60,
  });

  const dataFetchedRef = React.useRef(false);

  React.useEffect(() => {
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;

    const fetchSettings = async () => {
      try {
        const { ApiService } = await import("../../services/api");
        const planningSettings = await ApiService.getPlanningSettings();

        if (planningSettings) {
          setSettings(prev => ({
            ...prev,
            workDaysPerWeek: planningSettings.workingDays || 5,
            maxHoursPerDay: planningSettings.dailyMaxLessons || 8,
            maxConsecutiveHours: planningSettings.maxConsecutiveLessons || 3,
            lessonDuration: planningSettings.lessonDuration || 40,
            breakDuration: planningSettings.breakBetweenLessons || 10,
            schoolStartTime: planningSettings.schoolStartTime || '08:30',
            lunchBreakStart: planningSettings.lunchBreakStart || '12:00',
            lunchBreakDuration: planningSettings.lunchBreakDuration || 60,
            considerTeacherPreferences: planningSettings.considerTeacherPreferences ?? true,
            balanceWorkload: planningSettings.balanceWorkload ?? true,
            avoidConsecutiveLessons: planningSettings.preventConsecutiveSameLessons ?? false,
            maxDailySameLesson: planningSettings.maxDailySameLesson ?? 0,
          }));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { ApiService } = await import("../../services/api");

      const allDaysList = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const numDays = Number(settings.workDaysPerWeek) || 5;
      const workingDaysList = allDaysList.slice(0, numDays);

      const payload = {
        workingDays: numDays,
        dailyMaxLessons: Number(settings.maxHoursPerDay) || 8,
        maxConsecutiveLessons: Number(settings.maxConsecutiveHours) || 3,
        lessonDuration: Number(settings.lessonDuration) || 40,
        breakBetweenLessons: Number(settings.breakDuration) || 10,
        schoolStartTime: settings.schoolStartTime || '08:30',
        lunchBreakStart: settings.lunchBreakStart || '12:00',
        lunchBreakDuration: Number(settings.lunchBreakDuration) || 60,
        considerTeacherPreferences: Boolean(settings.considerTeacherPreferences),
        balanceWorkload: Boolean(settings.balanceWorkload),
        preventConsecutiveSameLessons: Boolean(settings.avoidConsecutiveLessons),
        minBreakBetweenLessons: Number(settings.minBreakBetweenLessons) || 0,
        maxDailySameLesson: Number(settings.maxDailySameLesson) || 0,
        workingDaysList
      };

      await ApiService.updatePlanningSettings(payload);
      setSuccessModalOpen(true);
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setErrorMessage(error?.message || 'Ayarlar kaydedilirken bir hata oluştu.');
      setErrorModalOpen(true);
    } finally {
      setSavingSettings(false);
    }
  };

  const renderSettings = () => {
    const fieldClassName =
      "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
    const selectFieldClassName =
      "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
    const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
    const sectionBadgeClassName =
      "inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700";
    const toggleRowClass =
      "flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200/90 bg-slate-50 p-4 text-left transition-colors hover:border-slate-300 hover:bg-white";

    return (
      <div className="p-5 sm:p-6">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">Planlama Ayarları</h1>
                <p className="mt-1 text-sm text-gray-600">Ders programı oluşturma parametrelerini yapılandırın.</p>
                <div className="mt-3 h-1 w-20 rounded-full bg-blue-200" />
              </div>
            </div>

            <form
              id="planning-settings-form"
              className="space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                void saveSettings();
              }}>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
                <div className="min-w-0 space-y-6">
              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>Temel Ayarlar</span>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Calendar size={14} className="text-gray-400" />
                      Haftalık Çalışma Günü
                    </label>
                    <select
                      value={settings.workDaysPerWeek}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          workDaysPerWeek: e.target.value === "" ? "" : parseInt(e.target.value),
                        }))
                      }
                      className={selectFieldClassName}>
                      <option value={5}>5 Gün (Pzt-Cum)</option>
                      <option value={6}>6 Gün (Pzt-Cmt)</option>
                      <option value={7}>7 Gün (Pzt-Paz)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <div className="mb-2 flex items-center gap-1.5">
                        <BookOpen size={14} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">Günlük Maksimum Ders</span>
                        <div className="group relative flex items-center">
                          <Info size={13} className="cursor-help text-blue-500" />
                          <div className="pointer-events-none invisible absolute bottom-full left-1/2 z-[100] mb-2 w-56 -translate-x-1/2 rounded-xl border border-gray-100 bg-white p-3 text-[11px] text-gray-600 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:opacity-100 sm:w-64">
                            <p className="leading-relaxed">
                              Bir sınıfın veya öğretmenin bir günde girebileceği toplam ders saati üst sınırını belirler.
                            </p>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white drop-shadow-[0_1px_0_rgba(243,244,246,1)]" />
                          </div>
                        </div>
                      </div>
                      <input
                        type="number"
                        value={settings.maxHoursPerDay}
                        onChange={(e) => setSettings((prev) => ({ ...prev, maxHoursPerDay: e.target.value }))}
                        className={fieldClassName}
                      />
                    </div>
                    <div>
                      <div className="mb-2 flex items-center gap-1.5">
                        <Layers size={14} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">Maksimum Ardışık Ders</span>
                        <div className="group relative flex items-center">
                          <Info size={13} className="cursor-help text-blue-500" />
                          <div className="pointer-events-none invisible absolute bottom-full left-1/2 z-[100] mb-2 w-56 -translate-x-1/2 rounded-xl border border-gray-100 bg-white p-3 text-[11px] text-gray-600 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:opacity-100 sm:w-64">
                            <p className="leading-relaxed">
                              Bir öğretmen veya sınıfın hiç ara vermeden (blok) girebileceği en fazla ders sayısını belirler.
                              <br />
                              <br />
                              <span className="mt-1.5 block border-t border-blue-50 pt-1.5 font-medium text-blue-600">
                                Örn: "3" yazılırsa, 3 ders peş peşe geldikten sonra araya boşluk konulmaya çalışılır.
                              </span>
                            </p>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white drop-shadow-[0_1px_0_rgba(243,244,246,1)]" />
                          </div>
                        </div>
                      </div>
                      <input
                        type="number"
                        value={settings.maxConsecutiveHours}
                        onChange={(e) => setSettings((prev) => ({ ...prev, maxConsecutiveHours: e.target.value }))}
                        className={fieldClassName}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <div className="mb-2 flex items-center gap-1.5">
                        <BookOpen size={14} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">Maksimum Günlük Aynı Ders</span>
                        <div className="group relative flex items-center">
                          <Info size={13} className="cursor-help text-blue-500" />
                          <div className="pointer-events-none invisible absolute bottom-full left-1/2 z-[100] mb-2 w-56 -translate-x-1/2 rounded-xl border border-gray-100 bg-white p-3 text-[11px] text-gray-600 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:opacity-100 sm:w-64">
                            <p className="leading-relaxed">
                              Bir sınıfa aynı dersin bir gün içinde en fazla kaç kez atanabileceğini belirler.
                              <br />
                              <br />
                              <span className="mt-1.5 block border-t border-blue-50 pt-1.5 font-medium text-blue-600">
                                Örn: "2" yazılırsa, aynı ders bir günde en fazla 2 kez gösterilir. "0" yazılırsa sınırlama uygulanmaz.
                              </span>
                            </p>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white drop-shadow-[0_1px_0_rgba(243,244,246,1)]" />
                          </div>
                        </div>
                      </div>
                      <input
                        type="number"
                        value={settings.maxDailySameLesson}
                        onChange={(e) => setSettings((prev) => ({ ...prev, maxDailySameLesson: e.target.value }))}
                        className={fieldClassName}
                        placeholder="0 = Sınırsız"
                      />
                    </div>
                    <div>
                      <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                        <Timer size={14} className="text-gray-400" />
                        Ders Süresi (dk)
                      </label>
                      <input
                        type="number"
                        value={settings.lessonDuration}
                        onChange={(e) => setSettings((prev) => ({ ...prev, lessonDuration: e.target.value }))}
                        className={fieldClassName}
                      />
                    </div>
                    <div>
                      <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                        <Coffee size={14} className="text-gray-400" />
                        Tenefüs Süresi (dk)
                      </label>
                      <input
                        type="number"
                        value={settings.breakDuration}
                        onChange={(e) => setSettings((prev) => ({ ...prev, breakDuration: e.target.value }))}
                        className={fieldClassName}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={sectionClassName}>
                <div className="mb-3">
                  <span className={sectionBadgeClassName}>Okul Saatleri ve Molalar</span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <Clock size={14} className="text-gray-400" />
                      Okul Başlangıç Saati
                    </label>
                    <input
                      type="time"
                      value={settings.schoolStartTime}
                      onChange={(e) => setSettings((prev) => ({ ...prev, schoolStartTime: e.target.value }))}
                      className={fieldClassName}
                    />
                  </div>
                  <div>
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <Coffee size={14} className="text-gray-400" />
                      Öğle Arası Başlangıç Saati
                    </label>
                    <input
                      type="time"
                      value={settings.lunchBreakStart}
                      onChange={(e) => setSettings((prev) => ({ ...prev, lunchBreakStart: e.target.value }))}
                      className={fieldClassName}
                    />
                  </div>
                  <div>
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <Timer size={14} className="text-gray-400" />
                      Öğle Arası Süresi (dk)
                    </label>
                    <input
                      type="number"
                      value={settings.lunchBreakDuration}
                      onChange={(e) => setSettings((prev) => ({ ...prev, lunchBreakDuration: e.target.value }))}
                      className={fieldClassName}
                    />
                  </div>
                  <div>
                    <div className="mb-2 flex items-center gap-1.5">
                      <Timer size={14} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">Dersler Arası Zorunlu Boşluk</span>
                      <div className="group relative flex items-center">
                        <Info size={13} className="cursor-help text-blue-500" />
                        <div className="pointer-events-none invisible absolute bottom-full left-1/2 z-[100] mb-2 w-56 -translate-x-1/2 rounded-xl border border-gray-100 bg-white p-3 text-[11px] text-gray-600 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:opacity-100 sm:w-64">
                          <p className="leading-relaxed">
                            Bir öğretmene veya sınıfa ardışık dersler atamak yerine arada en az kaç ders saati boşluk bırakılacağını belirler.
                            <br />
                            <br />
                            <span className="mt-1.5 block border-t border-blue-50 pt-1.5 font-medium text-blue-600">
                              Örn: "1" yazılırsa her dersin arasına en az 1 saat boşluk koymaya çalışır.
                            </span>
                          </p>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-white drop-shadow-[0_1px_0_rgba(243,244,246,1)]" />
                        </div>
                      </div>
                    </div>
                    <input
                      type="number"
                      value={settings.minBreakBetweenLessons}
                      onChange={(e) => setSettings((prev) => ({ ...prev, minBreakBetweenLessons: e.target.value }))}
                      className={fieldClassName}
                      placeholder="Örn: 1"
                    />
                  </div>
                </div>
              </div>
                </div>

                <div className="min-w-0 lg:sticky lg:top-2 self-start">
              <div className={sectionClassName}>
                <div className="mb-3 flex items-center gap-2">
                  <span className={sectionBadgeClassName}>Otomatik Dağıtım</span>
                </div>
                <div className="space-y-3">
                  {(
                    [
                      { key: "autoDistribute", label: "Otomatik program dağıtımı", desc: "Dersleri otomatik olarak günlere dağıtır" },
                      {
                        key: "considerTeacherPreferences",
                        label: "Öğretmen tercihlerini dikkate al",
                        desc: "Müsaitlik bilgilerini programı oluştururken kullanır",
                      },
                      { key: "balanceWorkload", label: "İş yükünü dengele", desc: "Öğretmenler arasında ders yükünü eşit dağıtır" },
                      { key: "avoidConsecutiveLessons", label: "Ardışık dersleri önle", desc: "Aynı dersin ard arda gelmesini engeller" },
                    ] as const
                  ).map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSettings((prev) => ({ ...prev, [item.key]: !(prev as any)[item.key] }))}
                      className={toggleRowClass}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.label}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{item.desc}</p>
                      </div>
                      {(settings as any)[item.key] ? (
                        <ToggleRight size={28} className="shrink-0 text-blue-600" />
                      ) : (
                        <ToggleLeft size={28} className="shrink-0 text-gray-300" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/planning?tab=dashboard", { replace: true })}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={savingSettings}
                    className="rounded-md bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm text-white hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60">
                    {savingSettings ? "Kaydediliyor..." : "Ayarları Kaydet"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const dashboardCategories = {
    bilgiler: {
      title: "Bilgiler",
      cards: [
        {
          id: "lessons",
          title: "Dersler",
          icon: BookOpen,
          color: "#3b82f6",
          description: "Toplam ders sayısı",
        },
        {
          id: "teachers",
          title: "Öğretmenler",
          icon: GraduationCap,
          color: "#f59e0b",
          description: "Toplam öğretmen sayısı",
        },
        {
          id: "students",
          title: "Öğrenciler",
          icon: Users,
          color: "#8b5cf6",
          description: "Toplam öğrenci sayısı",
        },
        {
          id: "daily-lessons",
          title: "Günlük Ders Saatleri",
          icon: Calendar,
          color: "#ec4899",
          description: "Bugünkü toplam ders",
        },
      ],
    },
    atamalar: {
      title: "Atamalar",
      cards: [
        {
          id: "class-lesson-assignments",
          title: "Departman Ders Atamaları",
          icon: UserCheck,
          color: "#059669",
          description: "Departman ders atama işlemleri",
        },
        {
          id: "teacher-branch-assignments",
          title: "Öğretmen Branş Atamaları",
          icon: GraduationCap,
          color: "#2563eb",
          description: "Öğretmenlere girecekleri dersleri tanımlayın",
        },
        {
          id: "teacher-lesson-assignments",
          title: "Öğretmen Ders Ataması",
          icon: UserPlus,
          color: "#dc2626",
          description: "Öğretmenlere ders atama işlemleri",
        },
      ],
    },
    programlar: {
      title: "Programlar",
      cards: [
        {
          id: "teacher-schedule",
          title: "Öğretmen Müsaitlikleri",
          icon: UserCheck,
          color: "#8b5cf6",
          description: "Öğretmen müsaitlikleri ve dağıtım işlemleri",
        },
        {
          id: "class-schedule",
          title: "Departman Programları",
          icon: Users,
          color: "#059669",
          description: "Departmanların haftalık ders programları",
        },
        {
          id: "teacher-programs",
          title: "Öğretmen Programları",
          icon: Calendar,
          color: "#f59e0b",
          description: "Öğretmenlerin haftalık ders programları",
        },
      ],
    },
  };

  const handleCardClick = (cardId: string) => {
    setActiveView(cardId);
  };

  const planningModuleTitles: Record<string, string> = {
    lessons: "Dersler",
    teachers: "Öğretmenler",
    students: "Öğrenciler",
    "daily-lessons": "Günlük Ders Saatleri",
    "class-lesson-assignments": "Departman Ders Atamaları",
    "teacher-branch-assignments": "Öğretmen Branş Atamaları",
    "teacher-lesson-assignments": "Öğretmen Ders Ataması",
    "teacher-schedule": "Öğretmen Müsaitlikleri",
    "teacher-programs": "Öğretmen Programları",
    "class-schedule": "Departman Programları",
  };

  const renderPlanningModule = (viewId: string, children: ReactNode) => {
    const title = planningModuleTitles[viewId] || "Planlama";
    return (
      <div className="relative flex h-full min-h-0 w-full flex-col bg-gray-50 sm:mr-1 sm:rounded-lg sm:shadow-sm">
        <header className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <button
                type="button"
                onClick={() => setActiveView("dashboard")}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-transparent px-2 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-900"
                aria-label="Geri dön">
                <ArrowLeft className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
                <span>Geri dön</span>
              </button>
              <span className="min-w-0 truncate text-xl font-semibold text-gray-900">{title}</span>
            </div>
          </div>
        </header>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-4 sm:px-6 sm:py-6">{children}</div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="p-6 space-y-10">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">Planlama Yönetimi</h2>
      </div>

      {Object.entries(dashboardCategories)
        .filter(([categoryKey, category]) => category.cards.length > 0)
        .map(([categoryKey, category]) => (
          <div key={categoryKey} className="space-y-4">
            <div className="flex items-center gap-3">
              <h3 className="shrink-0 text-lg font-semibold text-gray-800">{category.title}</h3>
              <div className="h-px min-w-0 flex-1 bg-gradient-to-r from-gray-200 via-gray-200/80 to-transparent" />
            </div>

            <div
              className={
                mainContentRightInsetActive
                  ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4"
                  : "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4"
              }>
              {category.cards.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => handleCardClick(card.id)}
                  className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-gray-200/90 bg-white text-left shadow-sm outline-none ring-blue-500/40 transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md focus-visible:ring-2">
                  <div
                    className="h-1 w-full shrink-0 opacity-90 transition-opacity group-hover:opacity-100"
                    style={{ background: `linear-gradient(90deg, ${card.color}, ${card.color}cc)` }}
                  />
                  <div className="flex flex-1 flex-col p-4">
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-black/5 transition-transform duration-200 group-hover:scale-[1.04]"
                      style={{
                        background: `linear-gradient(145deg, ${card.color}f2, ${card.color})`,
                      }}>
                      <card.icon size={20} strokeWidth={2} className="drop-shadow-sm" aria-hidden />
                    </div>
                    <h4 className="mt-3 text-sm font-semibold leading-snug text-gray-900 group-hover:text-gray-950">{card.title}</h4>
                    <p className="mt-1 line-clamp-2 flex-1 text-xs leading-relaxed text-gray-500">{card.description}</p>
                    <div className="mt-4 flex items-center gap-1 text-xs font-medium text-gray-400 transition-colors group-hover:text-blue-600">
                      <span>Detaya git</span>
                      <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
    </div>
  );

  const renderContent = () => {
    switch (activeView) {
      case "lessons":
        return renderPlanningModule("lessons", <LessonsList hidePageHeading />);
      case "teachers":
        return renderPlanningModule("teachers", <TeachersList hidePageHeading />);
      case "students":
        return renderPlanningModule("students", <StudentsList hidePageHeading />);
      case "daily-lessons":
        return renderPlanningModule("daily-lessons", <DailyLessons hidePageHeading />);
      case "teacher-lesson-assignments":
        return renderPlanningModule("teacher-lesson-assignments", <TeacherLessonAssignments />);
      case "teacher-branch-assignments":
        return renderPlanningModule("teacher-branch-assignments", <TeacherBranchAssignments />);
      case "class-lesson-assignments":
        return renderPlanningModule("class-lesson-assignments", <ClassLessonAssignments />);
      case "teacher-schedule":
        return renderPlanningModule("teacher-schedule", <TeacherSchedule hidePageHeading />);
      case "teacher-programs":
        return renderPlanningModule("teacher-programs", <TeacherSchedule showAllTeacherSchedulesByDefault={true} hidePageHeading />);
      case "class-schedule":
        return renderPlanningModule("class-schedule", <TeacherSchedule showClassScheduleByDefault={true} hidePageHeading />);

      default:
        return renderDashboard();
    }
  };

  const dashboardTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Ana Sayfa",
      path: "dashboard",
      customComponent: renderContent(),
    }),
    [activeView, mainContentRightInsetActive]
  );

  const settingsTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Ayarlar",
      path: "settings",
      customComponent: renderSettings(),
    }),
    [settings, savingSettings]
  );

  const tabs = useMemo(() => [dashboardTab, settingsTab], [dashboardTab, settingsTab]);

  return (
    <>
      <TabPage title="Planlama" tabs={tabs} />

      <ConfirmModal
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        onConfirm={() => setSuccessModalOpen(false)}
        title="Başarılı"
        message="Ayarlar başarıyla kaydedildi!"
        confirmText="Tamam"
        cancelText=""
        confirmButtonVariant="primary"
      />

      <ConfirmModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => setErrorModalOpen(false)}
        title="Hata"
        message={errorMessage}
        confirmText="Tamam"
        cancelText=""
        confirmButtonVariant="primary"
      />
    </>
  );
};

export default Planning;